import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, ExternalLink, CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';

interface StripeConnectButtonProps {
  entityType: 'organization' | 'club';
  entityId: string;
  entityName: string;
  variant?: 'default' | 'outline';
  size?: 'sm' | 'default' | 'lg';
}

export const StripeConnectButton: React.FC<StripeConnectButtonProps> = ({
  entityType,
  entityId,
  entityName,
  variant = 'default',
  size = 'default'
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Query existing Stripe account
  const { data: stripeAccount, refetch } = useQuery({
    queryKey: ['stripe-account', entityType, entityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stripe_accounts')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  const handleConnect = async () => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: {
          entity_type: entityType,
          entity_id: entityId,
          entity_name: entityName
        }
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe onboarding in new tab
        window.open(data.url, '_blank');
        // Refetch account status after connecting
        setTimeout(() => refetch(), 2000);
      }
    } catch (error) {
      console.error('Stripe Connect error:', error);
      toast({
        title: 'Error',
        description: 'Failed to start Stripe Connect onboarding',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('stripe-account-status', {
        body: {
          entity_type: entityType,
          entity_id: entityId
        }
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: 'Status Updated',
          description: 'Stripe account status has been refreshed',
        });
        // Refetch the query to update the UI
        refetch();
      } else {
        throw new Error(data?.error || 'Failed to refresh status');
      }
    } catch (error) {
      console.error('Error refreshing Stripe status:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh Stripe account status',
        variant: 'destructive'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Render account status if account exists
  if (stripeAccount) {
    const getStatusInfo = () => {
      if (stripeAccount.charges_enabled && stripeAccount.payouts_enabled) {
        return {
          icon: <CheckCircle className="h-4 w-4" />,
          text: 'Connected & Active',
          variant: 'default' as const,
          color: 'bg-green-100 text-green-800'
        };
      } else if (stripeAccount.onboarding_completed) {
        return {
          icon: <AlertCircle className="h-4 w-4" />,
          text: 'Connected (Pending Verification)',
          variant: 'secondary' as const,
          color: 'bg-yellow-100 text-yellow-800'
        };
      } else {
        return {
          icon: <Clock className="h-4 w-4" />,
          text: 'Onboarding Pending',
          variant: 'outline' as const,
          color: 'bg-blue-100 text-blue-800'
        };
      }
    };

    const statusInfo = getStatusInfo();

    return (
      <div className="space-y-2">
        <Badge className={`gap-2 ${statusInfo.color}`}>
          {statusInfo.icon}
          {statusInfo.text}
        </Badge>
        <div className="text-sm text-muted-foreground">
          <p>Account ID: {stripeAccount.stripe_account_id}</p>
          <p>Status: {stripeAccount.account_status}</p>
          <p>Last updated: {new Date(stripeAccount.updated_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleRefreshStatus}
            disabled={isRefreshing}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Status'}
          </Button>
          {!stripeAccount.onboarding_completed && (
            <Button
              onClick={handleConnect}
              disabled={isLoading}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <ExternalLink className="h-3 w-3" />
              {isLoading ? 'Connecting...' : 'Complete Setup'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Render connect button if no account exists
  return (
    <Button
      onClick={handleConnect}
      disabled={isLoading}
      variant={variant}
      size={size}
      className="gap-2"
    >
      <CreditCard className="h-4 w-4" />
      {isLoading ? 'Connecting...' : 'Connect Stripe'}
      <ExternalLink className="h-3 w-3" />
    </Button>
  );
};